import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useContext,
  useCallback
} from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button, message } from "antd";

import { ColDef } from "ag-grid-enterprise";
import {
  projectDateCellRenderer,
  IdLinkProjectSumbittalListComponent,
  projectOverviewCellRenderer,
  truncatedTextCellRenderer,
  dummyColumnDef
} from "components/cell-renders";
import { useAcceptProjectInvite } from "hooks/project";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { useHistory } from "react-router";
import { GridLoadingIndicator } from "components/widgets";
import { useQuery } from "@apollo/client";
import { QUERY_GET_SUBSCRIPTION_DETAILS } from "services/graphQL/queries";
import { DateUtils } from "utils/dateutils";
import ProjectActionsCell from "components/project-actions";
import ProjectsIcon from "components/svg-icons/projects-icon";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { getAccessToken, getProjectListV2 } from "services/auth";
import useCheckVersionUpdates from "hooks/check-version-updates";

import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { AcceptanceStatus, ErrorMessages } from "../../constants";
import { TProject } from "./models";
import {
  EProjectStatus,
  getProjectStatus,
  PROJECT_STATUS_COL_ID
} from "./project-status";

function ProjectLinkWithInviteRenderer(props: any) {
  const { data, onInviteAccepted } = props;
  const { acceptProjectInvite, acceptanceState } = useAcceptProjectInvite();

  const onAccept = () => {
    acceptProjectInvite(data.id);
  };

  useEffect(() => {
    if (acceptanceState.isCompleted) {
      onInviteAccepted();
    }
  }, [acceptanceState, onInviteAccepted]);

  return (
    <div className="w-full flex items-center justify-between">
      <div className="text-ellipsis overflow-hidden">
        <IdLinkProjectSumbittalListComponent {...props} />
      </div>
      {data.status_id === AcceptanceStatus.INVITED && (
        <div>
          <Button
            onClick={() => {
              onAccept();
            }}
            loading={acceptanceState.isLoading}
            disabled={acceptanceState.isLoading}
            data-testid="projectaggridlistcomponent-button-accept-invite"
          >
            Accept Project Invite
          </Button>
        </div>
      )}
    </div>
  );
}

function ProjectAgGridListComponent() {
  const history = useHistory();
  const subscriptionToken: any = getAccessToken();
  const {
    subscriptionRole,
    subscriptionId,
    eventLogs = []
  } = useContext(SubscriptionContext);

  useCheckVersionUpdates({
    autoCheckInIntervals: false
  });

  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [projectListData, setProjectListData] = useState<any>(null);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const { data: subscriptionDetail } = useQuery(
    QUERY_GET_SUBSCRIPTION_DETAILS,
    {
      variables: {
        id: subscriptionId
      }
    }
  );

  const fetchProjectListData = useCallback(async () => {
    try {
      const res = await getProjectListV2(subscriptionToken);

      if (res && res.success && Array.isArray(res.data?.data)) {
        setProjectListData([...res.data.data]);
      } else {
        message.error("An error occurred while fetching projects.");
        if (res?.error) {
          console.error("Project list fetch error:", res.error);
        }
      }
    } catch (error) {
      message.error("An unexpected error occurred.");
      console.error("Project list fetch exception:", error);
    }
  }, [subscriptionToken]);

  useEffect(() => {
    if (subscriptionId && subscriptionToken) {
      fetchProjectListData();
    }
    return () => {};
  }, [fetchProjectListData, subscriptionId, subscriptionToken]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some(
          (log) =>
            log.data_source === "projects" &&
            ["update", "create", "delete"].includes(log.change_type)
        )
      ) {
        fetchProjectListData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, fetchProjectListData]);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", !projectListData);
  }, [projectListData]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [isGridReady]);

  const onInviteAccepted = useCallback(() => {
    fetchProjectListData();
  }, [fetchProjectListData]);

  const onProjectChanged = useCallback(() => {
    fetchProjectListData();
  }, [fetchProjectListData]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        colId: "name",
        field: "name",
        headerName: "Project Name",
        headerTooltip: "Project Name",
        width: 280,
        minWidth: 120,
        maxWidth: 500,
        cellRenderer: ProjectLinkWithInviteRenderer,
        cellRendererParams: {
          onInviteAccepted
        },
        comparator: (valueA: any, valueB: any) => {
          return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        },
        sort: "asc"
      },
      {
        colId: "number",
        field: "number",
        headerName: "Project Number",
        headerTooltip: "Project Number",
        width: 160,
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "overview",
        headerName: "Record Counts",
        headerTooltip: "Record Counts",
        width: 230,
        minWidth: 230,
        maxWidth: 300,
        sortable: false,
        filter: false,
        cellRenderer: projectOverviewCellRenderer
      },
      {
        colId: "details",
        field: "details",
        headerName: "Details",
        headerTooltip: "Project Details",
        width: 250,
        minWidth: 120,
        maxWidth: 500,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "address",
        field: "address",
        headerName: "Address",
        headerTooltip: "Address",
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "start_date",
        field: "start_date",
        headerName: "Start Date",
        headerTooltip: "Start Date",
        minWidth: 120,
        cellRenderer: projectDateCellRenderer,
        cellRendererParams: { field: "start_date" },
        filter: CustomDateSetFilter,
        filterParams: {
          isCustomFilterOnly: true
        },
        getQuickFilterText(params: { data: TProject }) {
          return params.data.start_date
            ? DateUtils.format(params.data.start_date)
            : "";
        }
      },
      {
        colId: "end_date",
        field: "end_date",
        headerName: "End Date",
        headerTooltip: "End Date",
        minWidth: 120,
        cellRenderer: projectDateCellRenderer,
        cellRendererParams: { field: "end_date" },
        filter: CustomDateSetFilter,
        filterParams: {
          isCustomFilterOnly: true
        },
        getQuickFilterText(params: { data: TProject }) {
          return params.data.end_date
            ? DateUtils.format(params.data.end_date)
            : "";
        }
      },
      {
        colId: PROJECT_STATUS_COL_ID,
        headerName: "Project Status",
        headerTooltip: "Project Status",
        minWidth: 130,
        valueGetter: (params: { data?: TProject }) =>
          getProjectStatus(params.data),
        filter: "agSetColumnFilter",
        filterParams: {
          values: [EProjectStatus.Active, EProjectStatus.Archived]
        }
      },
      {
        colId: "actions",
        field: "actions",
        headerName: "Action",
        headerTooltip: "Action",
        headerClass: "ciq-center-header",
        cellClass: "ciq-no-cell-focus",
        pinned: "right",
        minWidth: 130,
        maxWidth: 150,
        sortable: false,
        filter: false,
        cellRenderer: ProjectActionsCell,
        cellRendererParams: {
          onProjectChanged
        },
        hide: subscriptionRole !== RoleSubscriptionEnum.subscription_admin
      }
    ];
  }, [onInviteAccepted, onProjectChanged, subscriptionRole]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
      editable: false,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const onFiltersApplied = () => {
    filterChipRef.current?.onFilterApplied();
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const renderProjects = () => {
    if (projectListData && projectListData?.length === 0) {
      return (
        <div className="h-[300px] flex items-center justify-center">
          <span>
            Start by creating projects or contact your Admin to invite you to a
            project
          </span>
        </div>
      );
    }

    return (
      <AgGridReact<any>
        ref={gridRef}
        onGridReady={() => {
          setGridReady(true);
          gridRef?.current?.api?.sizeColumnsToFit();
        }}
        onFirstDataRendered={() => {
          const api = gridRef?.current?.api;
          api?.sizeColumnsToFit();
          api
            ?.setColumnFilterModel(PROJECT_STATUS_COL_ID, {
              filterType: "set",
              values: [EProjectStatus.Active]
            })
            .then(() => api.onFilterChanged());
        }}
        rowData={projectListData}
        columnDefs={columnDefs} // Column Defs for Columns
        defaultColDef={defaultColDef} // Default Column Properties
        suppressContextMenu
        onFilterChanged={onFiltersApplied}
        onRowDataUpdated={() =>
          setStatsData(calculateLogPageStats({ gridRef }))
        }
        loadingOverlayComponent={GridLoadingIndicator}
        className="ag-theme-alpine"
        suppressScrollOnNewData
        suppressDragLeaveHidesColumns
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        tooltipShowMode="whenTruncated"
        selectionColumnDef={{
          width: 40
        }}
      />
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="SubscriptionProjects_search"
      gridRef={gridRef}
      placeholder="Search Projects"
    />,
    <FilterChipComponent
      key="SubscriptionProjects_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      key="SubscriptionProjects_AddNew"
      type="primary"
      title={
        !subscriptionDetail?.subscription_by_pk?.paid ||
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
          ? ErrorMessages.PermissionNotGranted
          : "Add Project"
      }
      onClick={() => history.push("/new-project")}
      disabled={
        !subscriptionDetail?.subscription_by_pk?.paid ||
        subscriptionRole <= RoleSubscriptionEnum.subscription_viewer
      }
      data-testid="projectaggridlistcomponent-button-add-project"
    >
      Add Project
    </Button>
  ];

  return (
    <>
      <CiqLogPageHeader
        {...{
          entityName: (
            <span className="flex items-center gap-3">
              <ProjectsIcon />
              Projects
            </span>
          ),
          titleParam: {
            title: "Projects",
            totalCount: projectListData?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />

      <div className="grow">{renderProjects()}</div>
    </>
  );
}

export default ProjectAgGridListComponent;
